import { makeAutoObservable, runInAction } from 'mobx'
import { normalizeApiError } from 'shared/api'
import { logger } from 'shared/lib'
import { DeveloperAuthApi } from 'entities/Auth/api'
import { type IOauthConfig, type IResponseApiScopes } from 'entities/Auth'

const resolveErrorMessage = (e: unknown, fallback: string) =>
  normalizeApiError(e).getError('message') || fallback

export class OauthPageStore {
  loading = true
  submitting = false
  error: string | null = null
  config: IOauthConfig | null = null
  private _params: Record<string, string> = {}
  private _redirecting = false
  private _disposed = false

  constructor(searchParams: URLSearchParams) {
    this._params = Object.fromEntries(searchParams.entries())

    makeAutoObservable(this)
  }

  get scopes(): IResponseApiScopes[] {
    return this.config?.scopes ?? []
  }

  get isReady() {
    return !this.loading && Boolean(this.config)
  }

  dispose = () => {
    this._disposed = true
  }

  private _redirect = (url?: string) => {
    if (!url || this._disposed) return

    this._redirecting = true
    window.location.assign(url)
  }

  init = async () => {
    try {
      runInAction(() => {
        this._disposed = false
        this.loading = true
        this.error = null
      })

      const { data } = await DeveloperAuthApi.getOauthConfig(this._params)

      if (data.redirect) {
        this._redirect(data.redirect)
        return
      }

      runInAction(() => {
        if (!data.config) {
          this.error = 'Failed to load the authorization request. Please try again.'
        }

        this.config = data.config ?? null
      })
    } catch (e) {
      runInAction(() => {
        this.error = resolveErrorMessage(
          e,
          'Failed to load the authorization request. Please try again.'
        )
      })

      logger.error(e)
    } finally {
      runInAction(() => {
        if (!this._redirecting) {
          this.loading = false
        }
      })
    }
  }

  authorize = () => this._submit()

  cancel = () => this._submit(true)

  private _submit = async (deny = false) => {
    if (this.submitting) return

    try {
      runInAction(() => {
        this.submitting = true
        this.error = null
      })

      const payload = {
        state: this._params.state ?? '',
        client_id: this.config?.client?.id ?? this._params.client_id ?? '',
      }

      const { data } = deny
        ? await DeveloperAuthApi.denyOauth(payload)
        : await DeveloperAuthApi.approveOauth(payload)

      runInAction(() => {
        if (data.redirect) {
          this._redirect(data.redirect)
        } else {
          this.error = 'Something went wrong. Please try again.'
        }
      })
    } catch (e) {
      runInAction(() => {
        this.error = resolveErrorMessage(e, 'Something went wrong. Please try again.')
      })

      logger.error(e)
    } finally {
      runInAction(() => {
        if (!this._redirecting) {
          this.submitting = false
        }
      })
    }
  }
}
